import { useEffect } from "react";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { z } from "zod";

import { useMe, useUpdateMe } from "@/api/me";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

const schema = z.object({
  firstName: z.string().max(150),
  lastName: z.string().max(150),
});

type FormValues = z.infer<typeof schema>;

export default function AccountCard() {
  const { t } = useTranslation();
  const meQuery = useMe();
  const update = useUpdateMe();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "" },
  });

  useEffect(() => {
    if (meQuery.data) {
      form.reset({
        firstName: meQuery.data.firstName ?? "",
        lastName: meQuery.data.lastName ?? "",
      });
    }
  }, [meQuery.data, form]);

  const onSubmit = (values: FormValues) => {
    update.mutate(values, {
      onSuccess: () => toast.success(t("profile.accountCard.saved")),
      onError: () => toast.error(t("profile.accountCard.saveFailed")),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.accountCard.title")}</CardTitle>
        <CardDescription>
          {t("profile.accountCard.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("profile.accountCard.firstNameLabel")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={150} className="w-64" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("profile.accountCard.lastNameLabel")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} maxLength={150} className="w-64" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormItem>
              <FormLabel>{t("profile.accountCard.emailLabel")}</FormLabel>
              <div className="text-sm text-gray-700">{meQuery.data?.email}</div>
              <FormDescription>
                {t("profile.accountCard.emailHint")}
              </FormDescription>
            </FormItem>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!form.formState.isDirty || update.isPending}
              >
                {t("common.buttons.save")}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
